import {
  BRAND_ICON_IDS,
  STARTER_ICON_IDS,
  STARTER_ICON_PATHS,
  type BrandIconId,
} from "@/components/business-unit/brandIcons.generated";

/**
 * The one vocabulary a business unit's icon is written in (NAV-103, NAV-116).
 *
 * There were two, and neither screen knew it. The unit's own settings page
 * offered a free text box and expected an emoji; the super admin's
 * business-unit list expected one of seven names from a general-purpose icon
 * set and translated ten known emoji onto them for older records. Anything
 * outside those ten — which is every emoji a person is actually likely to
 * choose — fell through to a generic building. So a BU admin picked an icon,
 * saved it, was told it had saved, and saw the old one still there: the value
 * was stored faithfully and rendered as something else, and the only honest
 * reading of that screen is that the save failed.
 *
 * The vocabulary is the brand icon library from the Navigator project plan —
 * the deck's own line icons, in the interface's navy, drawn as vectors so they
 * recolour. `brandIcons.generated.ts` holds the ids and names where they come
 * from; `extract_brand_icons.py` regenerates it.
 *
 * Both screens read and write these ids, and neither substitutes another. What
 * a unit already carries is *translated* rather than substituted — see
 * `normalizeBuIconId`, whose whole job is that a document written under either
 * of the old vocabularies still renders as something its admin would recognise.
 *
 * **Geometry arrives in two halves, and this module is the seam** (NAV-116).
 * The whole library is around 700 KiB of path data, which no page load should
 * carry for a picker only a unit's admin opens — so the eager module holds every
 * *id* plus the starter icons' paths, and the rest is fetched by
 * `loadBrandIconLibrary`. No other *application* module may import the library
 * module — the split is enforced by there being exactly one importer rather than
 * by a bundler rule. `buIcons.test.ts` imports it statically on purpose, to
 * check both halves at once; a test is not in the bundle.
 */
export { BRAND_ICON_IDS, STARTER_ICON_IDS };
export type { BrandIconId };

/** Where an unrecognised value lands, and what a unit that has never chosen
 *  shows. A plain office building says "a business unit" without claiming
 *  anything about which one. In the starter half by construction — the fallback
 *  must never be the icon that is still loading. */
export const DEFAULT_BU_ICON: BrandIconId = "office";

const BRAND_ICON_SET: ReadonlySet<string> = new Set(BRAND_ICON_IDS);

/**
 * What the two old vocabularies meant, in the new one.
 *
 * Two kinds of entry, and both are real data rather than hypothetical: the
 * emoji are what the unit settings page has been storing, and the bare names
 * are what the super admin's list has been storing. Neither screen can be
 * migrated away from without this, because a unit is not asked to re-pick its
 * icon just because we changed how icons are chosen.
 *
 * Generous on the emoji side on purpose. The old map knew ten, which is why
 * "anything a person is likely to choose" rendered as a generic building; the
 * whole complaint. Anything still unmatched falls back to `DEFAULT_BU_ICON`,
 * which is now a deliberate answer rather than the accident it used to be.
 *
 * **Nothing here may name a value that is now a brand id.** `normalizeBuIconId`
 * checks the id set first, so such an entry is dead: it promises a translation
 * that never runs. `microscope` was one — the old list wrote it and this map
 * turned it into `flask` — and widening the set to the whole library (NAV-116)
 * made it a real icon, so a unit that stored "microscope" now gets a microscope.
 * That is a better answer than the flask it used to be given, which is why the
 * entry was dropped rather than the id renamed. The test enumerates this map
 * against the id set so the next widening cannot leave a lie behind.
 */
const LEGACY_BU_ICONS: Readonly<Record<string, BrandIconId>> = {
  // The seven-plus named icons the super admin's list wrote.
  books: "book-open",
  library: "book",
  newspaper: "newspaper",
  laptop: "laptop",
  leaf: "leaf",
  graduation: "classroom",
  building: "office",
  globe: "globe",
  chart: "chart",
  target: "target",
  lightbulb: "lightbulb",
  // The emoji the unit settings page wrote.
  "🏢": "office",
  "🏬": "storefront",
  "🏪": "storefront",
  "🏠": "home",
  "🏡": "home",
  "🏛️": "office",
  "🏛": "office",
  "📚": "book-open",
  "📖": "book-open",
  "📕": "book",
  "📗": "book",
  "📘": "book",
  "📙": "book",
  "📓": "book",
  "📰": "newspaper",
  "🗞️": "newspaper",
  "🗞": "newspaper",
  "✏️": "pencil",
  "✏": "pencil",
  "🖊️": "pen-nib",
  "🖊": "pen-nib",
  "🖋️": "pen-nib",
  "🖋": "pen-nib",
  "🎨": "palette",
  "🎵": "music",
  "🎶": "music",
  "📷": "camera",
  "📸": "camera",
  "🎬": "camera",
  "🔬": "flask",
  "🧪": "flask",
  "⚗️": "flask",
  "⚗": "flask",
  "⚛️": "atom",
  "⚛": "atom",
  "🎓": "classroom",
  "🌍": "globe",
  "🌎": "globe",
  "🌏": "globe",
  "🌐": "globe",
  "🗺️": "map",
  "🗺": "map",
  "💼": "briefcase",
  "⚖️": "gavel",
  "⚖": "gavel",
  "📊": "chart",
  "📈": "chart",
  "🎯": "target",
  "🏆": "trophy",
  "💡": "lightbulb",
  "🚀": "rocket",
  "🧩": "puzzle",
  "🛡️": "shield",
  "🛡": "shield",
  "🍃": "leaf",
  "🌱": "leaf",
  "🌿": "leaf",
  "☁️": "cloud",
  "☁": "cloud",
  "👥": "team",
  "👨‍👩‍👧": "team",
  "💻": "laptop",
  "🖥️": "monitor",
  "🖥": "monitor",
  "📱": "mobile",
  "🗄️": "database",
  "🗄": "database",
};

/**
 * The brand icon a stored value means.
 *
 * Total: every input has an answer, so no screen can render an icon a
 * different screen would render differently. A value that is already a brand
 * id passes through; one of the two old vocabularies is translated; anything
 * else — a stray emoji, a value from a hand-edited document — is the default.
 *
 * Answers without a network round trip, for every one of the ids, which is why
 * the ids are in the eager module and only the geometry is split.
 */
export function normalizeBuIconId(
  stored: string | null | undefined,
): BrandIconId {
  if (!stored) return DEFAULT_BU_ICON;
  const value = stored.trim();
  if (BRAND_ICON_SET.has(value)) return value as BrandIconId;
  // Some emoji arrive with a variation selector and some without, and the two
  // are different strings. Both spellings are in the map above, but stripping
  // it catches the combinations nobody listed.
  const bare = value.replace(/\uFE0F/g, "");
  return LEGACY_BU_ICONS[value] ?? LEGACY_BU_ICONS[bare] ?? DEFAULT_BU_ICON;
}

/**
 * What a settings save should store for the icon.
 *
 * The picked one when somebody picked; otherwise whatever the unit already
 * had, **untouched**. That second half is the whole function: a stored value
 * outside the translation map normalises to `DEFAULT_BU_ICON` for display, and
 * writing that back — which is what saving the unit's *name* would do —
 * destroys the only record of what was originally chosen. Nothing visible
 * changes, which is what makes it the bad kind of data loss.
 *
 * Migrating a unit onto this vocabulary is a deliberate act: a click on the
 * picker, or a config apply. Not a side effect of editing the field above it.
 */
export function iconToStore(
  picked: BrandIconId | null,
  stored: string | null | undefined,
): string {
  return picked ?? (stored?.trim() || DEFAULT_BU_ICON);
}

/** What this icon is called, wherever it needs a name — the picker's search,
 *  its tooltip and the label a screen reader reads. Its own namespace rather
 *  than a section of `common`, because three hundred names is not a thing to
 *  read past while looking for a button's caption. */
export function buIconLabelKey(icon: BrandIconId): string {
  return `buIcons:${icon}`;
}

/** Every legacy spelling, for the test that checks they all name a real icon.
 *  Exported rather than asserted here so the check can enumerate the map
 *  instead of restating it. */
export const LEGACY_BU_ICON_ENTRIES: readonly (readonly [string, BrandIconId])[] =
  Object.entries(LEGACY_BU_ICONS);

// ─── The geometry, and the half of it that arrives on demand ───────────────

const STARTER_SET: ReadonlySet<string> = new Set(STARTER_ICON_IDS);

/** Filled in once, by the import below. Module state rather than React state
 *  because the answer is the same for the whole tab and outlives any
 *  component's lifetime. */
let libraryPaths: Record<string, readonly string[]> | null = null;
/** The in-flight import, so twenty icons mounting at once fetch one chunk.
 *  Cleared on failure so a later mount can retry — a dropped connection
 *  should not make the rest of the library unreachable for the session. */
let libraryLoad: Promise<void> | null = null;

/** Whether an id draws immediately. False for the library half until
 *  `loadBrandIconLibrary` has resolved. */
export function isStarterIcon(icon: BrandIconId): boolean {
  return STARTER_SET.has(icon);
}

/** True once the library half is in memory. */
export function brandIconLibraryLoaded(): boolean {
  return libraryPaths !== null;
}

/**
 * The paths for one icon, or `undefined` when its half has not arrived.
 *
 * `undefined` is drawn as *nothing*, never as another icon. That is the NAV-103
 * lesson applied to a new mechanism: a unit's name is always beside its icon, so
 * a blank beat says "still loading" and reads as such — while a different
 * picture in the same place is indistinguishable from a save that did not take,
 * which is the bug this whole vocabulary exists to have fixed.
 */
export function brandIconPaths(
  icon: BrandIconId,
): readonly string[] | undefined {
  return STARTER_ICON_PATHS[icon] ?? libraryPaths?.[icon];
}

/**
 * Fetch the library half, at most once per tab.
 *
 * The only place application code reaches `brandIconsLibrary.generated.ts`. A
 * second static importer in the app would put all of it back in the main bundle
 * with nothing to warn you, which is why that module's own header says so too.
 *
 * Components do not call this directly — `useBrandIconLibrary` does, and it is
 * what turns the arrival into a re-render.
 */
export function loadBrandIconLibrary(): Promise<void> {
  if (libraryPaths) return Promise.resolve();
  libraryLoad ??= import(
    "@/components/business-unit/brandIconsLibrary.generated"
  )
    .then((module) => {
      libraryPaths = module.LIBRARY_ICON_PATHS;
    })
    .catch((error: unknown) => {
      libraryLoad = null;
      throw error;
    });
  return libraryLoad;
}
